import {
  measureFirstItineraryRenderPerformance,
  measureHotelSelectionListRenderPerformance,
  measureActivitySelectionListRenderPerformance,
} from '@libs/utils';

import { isCrawlerUserAgent } from '../statsig/crawlerCheck';

import { getMetrics } from './metrics';

const enum PerformanceMetricTypeLegacy {
  PerformanceTripOverviewPageRendered = 'trip_overview_page_rendered',
  PerformanceItineraryRequested = 'performance_itinerary_requested',
  PerformanceItineraryReceived = 'performance_itinerary_received',
  PerformanceItineraryRendered = 'performance_itinerary_rendered',
  PerformanceItineraryTotalTimeToRender = 'performance_itinerary_total_time_to_render',

  PerformanceHotelListRequested = 'performance_hotel_list_requested',
  PerformanceHotelListReceived = 'performance_hotel_list_received',
  PerformanceHotelListRendered = 'performance_hotel_list_rendered',
  PerformanceHotelListTotalTimeToRender = 'performance_hotel_list_total_time_to_render',

  PerformanceActivityListRequested = 'performance_activity_list_requested',
  PerformanceActivityListReceived = 'performance_activity_list_received',
  PerformanceActivityListRendered = 'performance_activity_list_rendered',
  PerformanceActivityListTotalTimeToRender = 'performance_activity_list_total_time_to_render',
}

const metrics = getMetrics();
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const HISTOGRAM_THRESHOLDS = [50, 100, 200, 400, 800, 1600, 3200, 6400, 12800];
const performanceMetricLabels = {
  isCrawler:
    typeof navigator === 'undefined'
      ? 'false'
      : isCrawlerUserAgent(navigator.userAgent),
};

function trackPerformanceTripOverviewPageRendered(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricTypeLegacy.PerformanceTripOverviewPageRendered,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceItineraryRequested(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricTypeLegacy.PerformanceItineraryRequested,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceItineraryReceived(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricTypeLegacy.PerformanceItineraryReceived,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceItineraryRendered(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricTypeLegacy.PerformanceItineraryRendered,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceItineraryTotalTimeToRender(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricTypeLegacy.PerformanceItineraryTotalTimeToRender,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceHotelListRequested(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricTypeLegacy.PerformanceHotelListRequested,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceHotelListReceived(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricTypeLegacy.PerformanceHotelListReceived,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceHotelListRendered(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricTypeLegacy.PerformanceHotelListRendered,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceHotelListTotalTimeToRender(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricTypeLegacy.PerformanceHotelListTotalTimeToRender,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceActivityListRequested(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricTypeLegacy.PerformanceActivityListRequested,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceActivityListReceived(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricTypeLegacy.PerformanceActivityListReceived,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceActivityListRendered(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricTypeLegacy.PerformanceActivityListRendered,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceActivityListTotalTimeToRender(
  timeToEvent: number
): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricTypeLegacy.PerformanceActivityListTotalTimeToRender,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

export function trackPerformanceLegacy(): void {
  measureFirstItineraryRenderPerformance().then((measures) => {
    const {
      timeToTripOverviewPageRendered,
      timeToFirstItineraryAvailabilityRequestMs,
      timeToFirstItineraryAvailabilityReceivedMs,
      timeToFirstItineraryRenderedMs,
      totalTimeRequiredToRenderFirstItinerary,
    } = measures;

    trackPerformanceTripOverviewPageRendered(timeToTripOverviewPageRendered);
    trackPerformanceItineraryRequested(
      timeToFirstItineraryAvailabilityRequestMs
    );
    trackPerformanceItineraryReceived(
      timeToFirstItineraryAvailabilityReceivedMs
    );
    trackPerformanceItineraryRendered(timeToFirstItineraryRenderedMs);
    trackPerformanceItineraryTotalTimeToRender(
      totalTimeRequiredToRenderFirstItinerary
    );
  });

  measureHotelSelectionListRenderPerformance().then(
    ({
      timeToHotelSelectionViewAvailabilityRequestMs,
      timeToHotelSelectionViewAvailabilityReceivedMs,
      timeToHotelSelectionListRenderedMs,
      totalTimeRequiredToRenderHotelSelectionList,
    }) => {
      trackPerformanceHotelListRequested(
        timeToHotelSelectionViewAvailabilityRequestMs
      );
      trackPerformanceHotelListReceived(
        timeToHotelSelectionViewAvailabilityReceivedMs
      );
      trackPerformanceHotelListRendered(timeToHotelSelectionListRenderedMs);
      trackPerformanceHotelListTotalTimeToRender(
        totalTimeRequiredToRenderHotelSelectionList
      );
    }
  );

  measureActivitySelectionListRenderPerformance().then(
    ({
      timeToActivitySelectionViewAvailabilityRequestMs,
      timeToActivitySelectionViewAvailabilityReceivedMs,
      timeToActivitySelectionListRenderedMs,
      totalTimeRequiredToRenderActivitySelectionList,
    }) => {
      trackPerformanceActivityListRequested(
        timeToActivitySelectionViewAvailabilityRequestMs
      );
      trackPerformanceActivityListReceived(
        timeToActivitySelectionViewAvailabilityReceivedMs
      );
      trackPerformanceActivityListRendered(
        timeToActivitySelectionListRenderedMs
      );
      trackPerformanceActivityListTotalTimeToRender(
        totalTimeRequiredToRenderActivitySelectionList
      );
    }
  );
}
